import React, { useCallback, useEffect, useState } from "react";
import { LayoutList, Plus, Pencil, History, ChevronDown, ChevronRight, LayoutTemplate } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../../components/ui/select";
import CatalogTable from "../components/CatalogTable";
import { CatalogLoadingCard, CatalogErrorCard } from "../components/CatalogStateCard";
import EntityFormDialog from "../components/EntityFormDialog";
import StatusBadge from "../components/StatusBadge";
import { useEmailCatalogEntityList } from "../../../hooks/useEmailCatalogEntityList";
import {
  getEmailMarketingCampaigns,
  getEmailMarketingAngles,
  getCreatives,
  createCreative,
  updateCreativeMetadata,
  createCreativeVersion,
  getCreativeVersions,
} from "../../../lib/emailMarketingApi";
import CreativeCompositionFields from "./CreativeCompositionFields";
import { COMPOSITION_SLOTS, emptyComposition, compositionFromFingerprint, isCompositionComplete } from "./compositionSlots";
import CreativeEmailBuilder from "./builder/CreativeEmailBuilder";

/**
 * CreativesPanel — EMAILS-IMPL-011
 *
 * Governed Creative authoring: browse the persistent operational catalog
 * (EMAILS-IMPL-010's own `emails: full`-gated routes -- never the
 * War-Room-gated `/creative-analytics/creatives*` rollup), create a
 * Creative (parent + initial immutable Version 1.0.0), edit parent
 * metadata only (name/status -- composition is never PATCHable), and
 * create new immutable Versions with a full composition editor prefilled
 * from the current Version.
 *
 * Creative's OWN 7-state status vocabulary (Draft/Review/Approved/
 * Control/Challenger/Retired/Archived) is never conflated with any Asset
 * family's 5-/6-state vocabulary (CRE-SPEC-001 §24; see
 * email_marketing_creatives.CreativeStatus).
 */
export const CREATIVE_STATUS_VALUES = ["Draft", "Review", "Approved", "Control", "Challenger", "Retired", "Archived"];

const NO_ANGLE = "__none__";

function emptyParentForm() {
  return { name: "", status: "Draft", campaignId: "", angleId: NO_ANGLE, channel: "" };
}

function normalizeImagePresentation(composition) {
  if (!composition.image) return null;
  const p = composition.imagePresentation;
  if (!p) return null;
  const placement = (p.placement || "").trim() || null;
  const width = p.width !== "" && p.width !== null && p.width !== undefined ? Number(p.width) : null;
  const alignment = p.alignment || null;
  if (!placement && !width && !alignment) return null;
  return { placement, width, alignment };
}

function submitComposition(composition) {
  return { ...composition, imagePresentation: normalizeImagePresentation(composition) };
}

export default function CreativesPanel() {
  const listFn = useCallback(() => getCreatives(), []);
  const createFn = useCallback(
    ({ name, status, campaignId, angleId, channel, composition }) =>
      createCreative({ name, status, campaignId, angleId, channel, composition: submitComposition(composition) }),
    []
  );
  const updateFn = useCallback((id, { name, status }) => updateCreativeMetadata(id, { name, status }), []);
  const createVersionFn = useCallback(
    (id, { version, composition, canvasLayout }) => createCreativeVersion(id, { version, composition: submitComposition(composition), canvasLayout }),
    []
  );
  const getIdFn = useCallback((row) => row.creative_id, []);

  const { items, loading, error, mutating, mutationError, clearMutationError, createItem, updateItem, createVersionItem } =
    useEmailCatalogEntityList({ list: listFn, create: createFn, update: updateFn, createVersion: createVersionFn, getId: getIdFn });

  // Campaign/Angle reference data -- fetched once, mirroring CampaignsPanel's
  // own Program/Angle reference-fetch pattern exactly.
  const [campaigns, setCampaigns] = useState([]);
  const [angles, setAngles] = useState([]);
  const [refLoading, setRefLoading] = useState(true);
  const [refError, setRefError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [c, a] = await Promise.all([getEmailMarketingCampaigns(), getEmailMarketingAngles()]);
        if (!cancelled) {
          setCampaigns(c || []);
          setAngles(a || []);
        }
      } catch {
        if (!cancelled) setRefError("Failed to load Campaigns/Angles for the Creative form.");
      } finally {
        if (!cancelled) setRefLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const campaignName = (id) => campaigns.find((c) => c.campaign_id === id)?.name;
  const angleName = (id) => angles.find((a) => a.angle_id === id)?.name;

  const [expandedId, setExpandedId] = useState(null);
  const [historyOpenId, setHistoryOpenId] = useState(null);
  const [history, setHistory] = useState({}); // creativeId -> {status, versions}

  const [createOpen, setCreateOpen] = useState(false);
  const [createParentForm, setCreateParentForm] = useState(emptyParentForm());
  const [createComposition, setCreateComposition] = useState(emptyComposition());
  const [clientError, setClientError] = useState(null);

  const [editingRow, setEditingRow] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", status: "Draft" });

  const [versioningRow, setVersioningRow] = useState(null);
  const [versionForm, setVersionForm] = useState({ version: "", composition: emptyComposition() });

  // EMAILS-IMPL-015: the Email Builder opens FROM the Creative detail
  // (or from a historical Version in the existing History list) --
  // deliberately not a new top-level Emails tab (see EmailsShell.jsx's
  // own comment) and not a new route; a local view-state swap within
  // this same panel, matching how the rest of this panel already works.
  const [builderCreativeId, setBuilderCreativeId] = useState(null);
  const [builderHistoricalVersion, setBuilderHistoricalVersion] = useState(null);

  const openCreate = () => {
    setCreateParentForm(emptyParentForm());
    setCreateComposition(emptyComposition());
    setClientError(null);
    clearMutationError();
    setCreateOpen(true);
  };

  const submitCreate = async () => {
    setClientError(null);
    if (!isCompositionComplete(createComposition)) {
      setClientError({ message: "All required composition slots (Subject, Preview, Hook, Email Body, Offer, CTA, Landing Page) must be selected." });
      return;
    }
    try {
      await createItem({
        name: createParentForm.name.trim(),
        status: createParentForm.status,
        campaignId: createParentForm.campaignId,
        angleId: createParentForm.angleId === NO_ANGLE ? null : createParentForm.angleId,
        channel: createParentForm.channel.trim() || null,
        composition: createComposition,
      });
      setCreateOpen(false);
    } catch {
      // mutationError already surfaced; keep dialog open
    }
  };

  const openEdit = (row) => {
    setEditingRow(row);
    setEditForm({ name: row.name, status: row.status });
    clearMutationError();
  };

  const submitEdit = async () => {
    try {
      await updateItem(editingRow.creative_id, {
        name: editForm.name.trim() !== editingRow.name ? editForm.name.trim() : undefined,
        status: editForm.status !== editingRow.status ? editForm.status : undefined,
      });
      setEditingRow(null);
    } catch {
      // dialog stays open
    }
  };

  const openVersion = (row) => {
    setVersioningRow(row);
    setVersionForm({ version: "", composition: row.fingerprint ? compositionFromFingerprint(row.fingerprint) : emptyComposition() });
    setClientError(null);
    clearMutationError();
  };

  const submitVersion = async () => {
    setClientError(null);
    if (!versionForm.version.trim()) {
      setClientError({ message: "A new Version string is required (e.g. 2.0.0)." });
      return;
    }
    if (!isCompositionComplete(versionForm.composition)) {
      setClientError({ message: "All required composition slots (Subject, Preview, Hook, Email Body, Offer, CTA, Landing Page) must be selected." });
      return;
    }
    try {
      await createVersionItem(versioningRow.creative_id, { version: versionForm.version.trim(), composition: versionForm.composition });
      setVersioningRow(null);
    } catch {
      // dialog stays open (e.g. 409 version conflict)
    }
  };

  const toggleHistory = async (creativeId) => {
    if (historyOpenId === creativeId) {
      setHistoryOpenId(null);
      return;
    }
    setHistoryOpenId(creativeId);
    if (!history[creativeId]) {
      setHistory((h) => ({ ...h, [creativeId]: { status: "loading", versions: [] } }));
      try {
        const versions = await getCreativeVersions(creativeId);
        setHistory((h) => ({ ...h, [creativeId]: { status: "ready", versions: versions || [] } }));
      } catch {
        setHistory((h) => ({ ...h, [creativeId]: { status: "error", versions: [] } }));
      }
    }
  };

  const openBuilder = (row) => {
    setBuilderHistoricalVersion(null);
    setBuilderCreativeId(row.creative_id);
  };

  const viewHistoricalInBuilder = (row, versionEntry) => {
    setBuilderHistoricalVersion(versionEntry);
    setBuilderCreativeId(row.creative_id);
  };

  const closeBuilder = () => {
    setBuilderCreativeId(null);
    setBuilderHistoricalVersion(null);
  };

  const handleBuilderSave = (creativeId, draft, version) =>
    createVersionItem(creativeId, { version, composition: draft.fingerprint, canvasLayout: draft.canvasLayout });

  const handleBuilderViewHistory = (row) => {
    closeBuilder();
    setExpandedId(row.creative_id);
    if (historyOpenId !== row.creative_id) toggleHistory(row.creative_id);
  };

  if (loading || refLoading) return <CatalogLoadingCard testId="creatives-loading" />;
  if (error) return <CatalogErrorCard message={error} testId="creatives-error" />;
  if (refError) return <CatalogErrorCard message={refError} testId="creatives-ref-error" />;

  if (builderCreativeId) {
    const builderRow = items.find((r) => r.creative_id === builderCreativeId);
    if (!builderRow) {
      return <CatalogErrorCard message={`Creative ${builderCreativeId} is no longer available.`} testId="creatives-builder-missing" />;
    }
    return (
      <CreativeEmailBuilder
        creativeRow={builderRow}
        historicalEntry={builderHistoricalVersion}
        onClose={closeBuilder}
        onSave={handleBuilderSave}
        saving={mutating}
        saveError={mutationError}
        onClearSaveError={clearMutationError}
        onViewHistory={() => handleBuilderViewHistory(builderRow)}
      />
    );
  }

  const noCampaigns = campaigns.length === 0;

  return (
    <div className="space-y-4" data-testid="creatives-panel">
      <CatalogTable
        testId="creatives-table"
        title="Creatives"
        icon={LayoutList}
        headerAction={
          <Button
            type="button"
            size="sm"
            className="gap-1.5 bg-violet-600 text-white hover:bg-violet-500"
            onClick={openCreate}
            disabled={noCampaigns}
            title={noCampaigns ? "Create a Campaign first" : undefined}
            data-testid="new-creative-button"
          >
            <Plus className="h-4 w-4" /> New Creative
          </Button>
        }
        rows={items}
        getRowKey={(row) => row.creative_id}
        onRowClick={(row) => setExpandedId(expandedId === row.creative_id ? null : row.creative_id)}
        emptyTitle="No Creatives yet"
        emptySubtitle={noCampaigns ? "Create a Campaign first, then create a Creative." : 'Create one with "New Creative" to get started.'}
        columns={[
          {
            key: "name",
            label: "Creative",
            render: (row) => (
              <div>
                <div className="text-sm font-medium text-slate-100">{row.name}</div>
                <div className="text-xs text-slate-400">{row.creative_id}</div>
              </div>
            ),
          },
          { key: "status", label: "Status", render: (row) => <StatusBadge status={row.status} testId={`creatives-status-${row.creative_id}`} /> },
          {
            // EMAILS-IMPL-027: labeled "Origin Campaign" -- this is the
            // Campaign the Creative was originally authored under
            // (provenance), never an exclusive-ownership/execution
            // restriction. A Broadcast may reference this Creative under
            // ANY Campaign; see CRE-SPEC-001 §23.8.
            key: "campaign",
            label: "Origin Campaign",
            render: (row) => <span className="text-sm text-slate-300">{campaignName(row.campaign_id) || row.campaign_id}</span>,
          },
          { key: "angle", label: "Angle", render: (row) => <span className="text-sm text-slate-300">{row.angle_id ? angleName(row.angle_id) || row.angle_id : "—"}</span> },
          { key: "current_version", label: "Current Version", render: (row) => <span className="font-mono text-sm text-slate-300">{row.current_version || "—"}</span> },
          { key: "updated_at", label: "Updated", render: (row) => <span className="text-sm text-slate-400">{row.updated_at ? new Date(row.updated_at).toLocaleString() : "—"}</span> },
        ]}
      />

      {items
        .filter((row) => row.creative_id === expandedId)
        .map((row) => (
          <div key={row.creative_id} className="rounded-lg border border-white/10 bg-white/5 p-4" data-testid={`creatives-detail-${row.creative_id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-4">
                  <Detail label="Governed ID" value={row.creative_id} mono />
                  <Detail label="Status" value={row.status || "—"} />
                  <Detail label="Current Version" value={row.current_version || "—"} mono />
                  <Detail label="Channel" value={row.channel || "—"} />
                  <Detail
                    label="Origin Campaign"
                    value={`${row.campaign_id}${campaignName(row.campaign_id) ? ` — ${campaignName(row.campaign_id)}` : ""}`}
                  />
                  <Detail label="Angle" value={row.angle_id ? `${row.angle_id}${angleName(row.angle_id) ? ` — ${angleName(row.angle_id)}` : ""}` : "Not assigned"} />
                  <Detail label="Updated" value={row.updated_at ? new Date(row.updated_at).toLocaleString() : "—"} />
                </div>
                <p className="text-xs text-slate-500" data-testid={`creatives-detail-provenance-note-${row.creative_id}`}>
                  Origin Campaign reflects where this Creative was originally created — it does not restrict which Campaign a
                  Broadcast may use this Creative under.
                </p>
                {row.fingerprint && (
                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-500">Composition (current version)</div>
                    <CompositionSummary fingerprint={row.fingerprint} testPrefix={`creatives-detail-${row.creative_id}`} />
                  </div>
                )}
                <div>
                  <button
                    type="button"
                    className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-violet-300 hover:text-violet-200"
                    onClick={() => toggleHistory(row.creative_id)}
                    data-testid={`creatives-history-toggle-${row.creative_id}`}
                  >
                    {historyOpenId === row.creative_id ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                    Version History
                  </button>
                  {historyOpenId === row.creative_id && (
                    <VersionHistoryList
                      entry={history[row.creative_id]}
                      testPrefix={`creatives-history-${row.creative_id}`}
                      onViewInBuilder={(versionEntry) => viewHistoricalInBuilder(row, versionEntry)}
                    />
                  )}
                </div>
              </div>
              <div className="flex shrink-0 flex-col gap-2">
                <Button type="button" size="sm" className="gap-1.5 bg-violet-600 text-white hover:bg-violet-500" onClick={() => openBuilder(row)} data-testid={`open-builder-${row.creative_id}`}>
                  <LayoutTemplate className="h-3.5 w-3.5" /> Open Builder
                </Button>
                <Button type="button" size="sm" variant="outline" className="gap-1.5 border-white/10 text-slate-200" onClick={() => openEdit(row)} data-testid={`edit-creative-${row.creative_id}`}>
                  <Pencil className="h-3.5 w-3.5" /> Edit Metadata
                </Button>
                <Button type="button" size="sm" variant="outline" className="gap-1.5 border-violet-400/30 text-violet-200" onClick={() => openVersion(row)} data-testid={`new-version-creative-${row.creative_id}`}>
                  <History className="h-3.5 w-3.5" /> Create New Version
                </Button>
              </div>
            </div>
          </div>
        ))}

      <EntityFormDialog
        testId="create-creative-dialog"
        open={createOpen}
        onOpenChange={setCreateOpen}
        title="New Creative"
        description="A governed composition (CRE####) referencing approved reusable Assets -- not raw email content."
        onSubmit={submitCreate}
        submitting={mutating}
        error={mutationError || clientError}
        submitLabel="Create Creative"
      >
        <div className="space-y-2">
          <Label htmlFor="creative-name">Name</Label>
          <Input id="creative-name" value={createParentForm.name} onChange={(e) => setCreateParentForm((f) => ({ ...f, name: e.target.value }))} required data-testid="creative-name-input" />
        </div>
        <CreativeStatusField value={createParentForm.status} onChange={(status) => setCreateParentForm((f) => ({ ...f, status }))} testId="creative-status-select" />
        <div className="space-y-2">
          <Label htmlFor="creative-campaign-select">Origin Campaign</Label>
          <Select value={createParentForm.campaignId} onValueChange={(v) => setCreateParentForm((f) => ({ ...f, campaignId: v }))}>
            <SelectTrigger id="creative-campaign-select" data-testid="creative-campaign-select">
              <SelectValue placeholder="Select a Campaign" />
            </SelectTrigger>
            <SelectContent>
              {campaigns.map((c) => (
                <SelectItem key={c.campaign_id} value={c.campaign_id} data-testid={`creative-campaign-option-${c.campaign_id}`}>
                  {c.name} ({c.campaign_id})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-500">
            The Campaign this Creative is being authored under. This Creative can later be used by a Broadcast under any
            Campaign — it is never restricted to this one.
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="creative-angle-select">Angle (optional)</Label>
          <Select value={createParentForm.angleId} onValueChange={(v) => setCreateParentForm((f) => ({ ...f, angleId: v }))}>
            <SelectTrigger id="creative-angle-select" data-testid="creative-angle-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_ANGLE}>No Angle</SelectItem>
              {angles.map((a) => (
                <SelectItem key={a.angle_id} value={a.angle_id} data-testid={`creative-angle-option-${a.angle_id}`}>
                  {a.name} ({a.angle_id})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="creative-channel">Channel (optional)</Label>
          <Input id="creative-channel" value={createParentForm.channel} onChange={(e) => setCreateParentForm((f) => ({ ...f, channel: e.target.value }))} placeholder="e.g. email" data-testid="creative-channel-input" />
        </div>
        <CreativeCompositionFields composition={createComposition} onChange={setCreateComposition} testPrefix="create-creative" />
      </EntityFormDialog>

      {editingRow && (
        <EntityFormDialog
          testId="edit-creative-dialog"
          open={!!editingRow}
          onOpenChange={(open) => !open && setEditingRow(null)}
          title={`Edit ${editingRow.creative_id}`}
          description='Rename or change the governed status of this Creative. Composition cannot be changed here -- use "Create New Version" for that.'
          onSubmit={submitEdit}
          submitting={mutating}
          error={mutationError}
          submitLabel="Save Changes"
        >
          <div className="space-y-2">
            <Label htmlFor="creative-edit-name">Name</Label>
            <Input id="creative-edit-name" value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} required data-testid="creative-edit-name-input" />
          </div>
          <CreativeStatusField value={editForm.status} onChange={(status) => setEditForm((f) => ({ ...f, status }))} testId="creative-edit-status-select" />
        </EntityFormDialog>
      )}

      {versioningRow && (
        <EntityFormDialog
          testId="new-version-creative-dialog"
          open={!!versioningRow}
          onOpenChange={(open) => !open && setVersioningRow(null)}
          title={`New Version — ${versioningRow.creative_id}`}
          description="Creates a new, immutable Version. The current Version stays historically accessible and unchanged."
          onSubmit={submitVersion}
          submitting={mutating}
          error={mutationError || clientError}
          submitLabel="Create New Version"
        >
          <div className="rounded-md border border-white/10 bg-black/20 p-3 text-sm">
            <div className="flex justify-between text-slate-400">
              <span>Creative</span>
              <span className="font-mono text-slate-200" data-testid="new-version-creative-id">{versioningRow.creative_id}</span>
            </div>
            <div className="mt-1 flex justify-between text-slate-400">
              <span>Current version</span>
              <span className="font-mono text-slate-200" data-testid="new-version-creative-current">{versioningRow.current_version || "—"}</span>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="creative-new-version">New Version</Label>
            <Input id="creative-new-version" value={versionForm.version} onChange={(e) => setVersionForm((f) => ({ ...f, version: e.target.value }))} placeholder="e.g. 2.0.0" required data-testid="creative-new-version-input" />
          </div>
          <CreativeCompositionFields composition={versionForm.composition} onChange={(composition) => setVersionForm((f) => ({ ...f, composition }))} testPrefix="new-version-creative" />
        </EntityFormDialog>
      )}
    </div>
  );
}

function CreativeStatusField({ value, onChange, testId }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={testId}>Status</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={testId} data-testid={testId}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {CREATIVE_STATUS_VALUES.map((s) => (
            <SelectItem key={s} value={s} data-testid={`${testId}-option-${s}`}>
              {s}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function CompositionSummary({ fingerprint, testPrefix }) {
  return (
    <div className="mt-1 space-y-1 rounded-md border border-white/10 bg-black/20 p-2" data-testid={`${testPrefix}-composition`}>
      {COMPOSITION_SLOTS.map((slot) => {
        const ref = fingerprint[slot.backendKey];
        return (
          <div key={slot.key} className="flex justify-between text-xs">
            <span className="text-slate-500">{slot.label}</span>
            <span className="font-mono text-slate-300" data-testid={`${testPrefix}-${slot.key}`}>
              {ref ? `${ref.asset_id} @ ${ref.version}` : "—"}
            </span>
          </div>
        );
      })}
      {fingerprint.image_presentation && (
        <div className="flex justify-between text-xs">
          <span className="text-slate-500">Image Presentation</span>
          <span className="text-slate-300">
            {[fingerprint.image_presentation.placement, fingerprint.image_presentation.width ? `${fingerprint.image_presentation.width}px` : null, fingerprint.image_presentation.alignment]
              .filter(Boolean)
              .join(" · ") || "—"}
          </span>
        </div>
      )}
    </div>
  );
}

function VersionHistoryList({ entry, testPrefix, onViewInBuilder }) {
  if (!entry || entry.status === "loading") {
    return <p className="mt-2 text-xs text-slate-500" data-testid={`${testPrefix}-loading`}>Loading version history…</p>;
  }
  if (entry.status === "error") {
    return <p className="mt-2 text-xs text-rose-300" data-testid={`${testPrefix}-error`}>Failed to load version history.</p>;
  }
  if (entry.versions.length === 0) {
    return <p className="mt-2 text-xs text-slate-500" data-testid={`${testPrefix}-empty`}>No version history available.</p>;
  }
  return (
    <div className="mt-2 space-y-2" data-testid={`${testPrefix}-list`}>
      {entry.versions.map((v) => (
        <div key={v.version} className="rounded-md border border-white/10 bg-black/20 p-2" data-testid={`${testPrefix}-item-${v.version}`}>
          <div className="flex items-center justify-between text-xs">
            <span className="font-mono text-slate-200">{v.version}</span>
            <div className="flex items-center gap-2">
              <span className="text-slate-500">{v.created_at ? new Date(v.created_at).toLocaleString() : "—"}</span>
              {onViewInBuilder && (
                <button
                  type="button"
                  className="text-violet-300 hover:text-violet-200"
                  onClick={() => onViewInBuilder(v)}
                  data-testid={`${testPrefix}-view-in-builder-${v.version}`}
                >
                  View in Builder
                </button>
              )}
            </div>
          </div>
          <CompositionSummary fingerprint={v.fingerprint} testPrefix={`${testPrefix}-item-${v.version}`} />
        </div>
      ))}
    </div>
  );
}

function Detail({ label, value, mono }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className={mono ? "font-mono text-slate-200" : "text-slate-200"}>{value}</div>
    </div>
  );
}
